import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  DESIGNERS_AUDIENCE,
  DESIGNERS_CONTACT,
  DESIGNERS_EXAMPLES,
  DESIGNERS_FEATURES,
  DESIGNERS_FINAL_CTA,
  DESIGNERS_HERO,
  DESIGNERS_LEAD_TYPES,
  DESIGNERS_LOGOS_STRIP,
  DESIGNERS_NAV,
  DESIGNERS_SEO,
  DESIGNERS_STEPS,
  DESIGNERS_WHOLESALE,
} from './designersLandingContent'
import {
  applyLandingSeo,
  buildRegisterPath,
  captureUtmParams,
  initLandingAnalytics,
  openLeadChannel,
  trackLandingCta,
  useLandingDarkThemeEffect,
} from './landingUtils'
import {
  LANDING_EXAMPLE_COUNT,
  DESIGNERS_HERO_PREVIEW,
  LANDING_LIB_IMAGES,
} from './landingMedia'
import BrandLogo from '../../components/BrandLogo/BrandLogo'
import './LandingPage.css'

type LeadForm = {
  name: string
  contact: string
  type: string
  comment: string
}

export default function DesignersLandingPage() {
  const navigate = useNavigate()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [formSent, setFormSent] = useState(false)
  const [form, setForm] = useState<LeadForm>({
    name: '',
    contact: '',
    type: 'freelance',
    comment: '',
  })

  const telegramUrl = import.meta.env.VITE_TELEGRAM_URL?.trim()
  const vkUrl = import.meta.env.VITE_VK_URL?.trim()
  const libImgs = LANDING_LIB_IMAGES

  useEffect(() => {
    captureUtmParams()
    initLandingAnalytics()
    applyLandingSeo({
      page: 'designers',
      title: DESIGNERS_SEO.title,
      description: DESIGNERS_SEO.description,
      path: DESIGNERS_SEO.path,
    })
  }, [])

  useEffect(useLandingDarkThemeEffect(), [])

  useEffect(() => {
    if (!mobileMenuOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileMenuOpen])

  const goRegister = (source: string) => {
    trackLandingCta('designers', source)
    navigate(buildRegisterPath('designers'))
  }

  const scrollToContact = (source: string) => {
    trackLandingCta('designers', source)
    setMobileMenuOpen(false)
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleTelegramLead = () => {
    trackLandingCta('designers', 'telegram')
    openLeadChannel(telegramUrl, DESIGNERS_CONTACT.mailSubject, DESIGNERS_CONTACT.telegramPrefill)
  }

  const handleLeadSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!form.name.trim() || !form.contact.trim()) return
    trackLandingCta('designers', 'form_submit')
    const typeLabel =
      DESIGNERS_LEAD_TYPES.find(t => t.value === form.type)?.label ?? form.type
    const body = [
      'Заявка с лендинга /designers',
      '',
      `Имя: ${form.name.trim()}`,
      `Контакт: ${form.contact.trim()}`,
      `Тип: ${typeLabel}`,
      form.comment.trim() ? `Комментарий: ${form.comment.trim()}` : '',
    ]
      .filter(Boolean)
      .join('\n')
    openLeadChannel(telegramUrl, DESIGNERS_CONTACT.mailSubject, body)
    setFormSent(true)
  }

  return (
    <div className="kmin bg-[#07070A] text-white min-h-screen font-sans overflow-hidden">
      <div className="kmin-glow">
        <div className="kmin-glow-top" />
        <div className="kmin-glow-bottom" />
      </div>

      <header className="kmin-nav">
        <div className="kmin-container">
          <div className="kmin-nav-inner">
            <BrandLogo variant="landing" to="/" />
            <nav className="kmin-nav-links" aria-label="Навигация для дизайнеров">
              {DESIGNERS_NAV.map(item => (
                <a key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="kmin-nav-actions">
              <button
                type="button"
                className="kmin-nav-burger"
                aria-label={mobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(v => !v)}
              >
                <span className="material-symbols-outlined">
                  {mobileMenuOpen ? 'close' : 'menu'}
                </span>
              </button>
              <button
                type="button"
                className="kmin-btn-primary kmin-nav-cta"
                onClick={() => scrollToContact('nav_apply')}
              >
                {DESIGNERS_HERO.primaryCta}
              </button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <>
            <button
              type="button"
              className="kmin-nav-mobile-backdrop"
              aria-label="Закрыть меню"
              onClick={() => setMobileMenuOpen(false)}
            />
            <nav className="kmin-nav-mobile-sheet" aria-label="Мобильное меню">
              {DESIGNERS_NAV.map(item => (
                <a key={item.href} href={item.href} onClick={() => setMobileMenuOpen(false)}>
                  {item.label}
                </a>
              ))}
              <button
                type="button"
                className="kmin-btn-primary kmin-btn-block"
                onClick={() => scrollToContact('nav_mobile_apply')}
              >
                {DESIGNERS_HERO.primaryCta}
              </button>
            </nav>
          </>
        )}
      </header>

      <section className="kmin-hero kmin-hero--designers">
        <div className="kmin-container">
          <div className="kmin-hero-grid">
            <div>
              <div className="kmin-badge">{DESIGNERS_HERO.badge}</div>
              <h1 className="kmin-hero-title">
                <span style={{ color: '#ff3495' }}>{DESIGNERS_HERO.titleAccent}</span>
                <span className="block">{DESIGNERS_HERO.titleRest}</span>
              </h1>
              <p className="kmin-hero-desc">{DESIGNERS_HERO.subtitle}</p>
              <div className="kmin-hero-actions">
                <button
                  type="button"
                  className="kmin-btn-primary kmin-btn-lg"
                  onClick={() => scrollToContact('hero_primary')}
                >
                  {DESIGNERS_HERO.primaryCta}
                </button>
                <button
                  type="button"
                  className="kmin-btn-outline kmin-btn-lg"
                  onClick={() => goRegister('hero_secondary')}
                >
                  {DESIGNERS_HERO.secondaryCta}
                </button>
              </div>
              <p className="kmin-hero-trial-badge">{DESIGNERS_HERO.trialBadge}</p>
            </div>

            <div className="kmin-hero-preview">
              <div className="kmin-hero-preview-glow" />
              <div className="kmin-hero-preview-card">
                <div className="kmin-preview-header">
                  <div>
                    <div className="font-medium">AI Студия</div>
                    <div className="kmin-preview-sub">{DESIGNERS_HERO.previewSub}</div>
                  </div>
                  <div className="kmin-preview-status">Онлайн</div>
                </div>
                <div className="kmin-preview-prompt">{DESIGNERS_HERO.previewPrompt}</div>
                <div className="kmin-preview-grid2">
                  {DESIGNERS_HERO_PREVIEW.large.map(i => (
                    <div
                      key={i}
                      className="kmin-preview-box"
                      onClick={() => setPreviewUrl(libImgs[i])}
                    >
                      <img
                        src={libImgs[i]}
                        alt={
                          i === 2
                            ? 'Пример рекламного плаката KartinAi'
                            : 'Пример визуала для клиента'
                        }
                        className="kmin-preview-img"
                        fetchPriority={i === DESIGNERS_HERO_PREVIEW.large[0] ? 'high' : undefined}
                        loading={i === DESIGNERS_HERO_PREVIEW.large[0] ? undefined : 'lazy'}
                      />
                    </div>
                  ))}
                </div>
                <div className="kmin-preview-grid3">
                  {DESIGNERS_HERO_PREVIEW.small.map(i => (
                    <div
                      key={i}
                      className="kmin-preview-box-sm"
                      onClick={() => setPreviewUrl(libImgs[i])}
                    >
                      <img
                        src={libImgs[i]}
                        alt="Пример визуала"
                        className="kmin-preview-img-sm"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="kmin-logos">
        <div className="kmin-container">
          <div className="kmin-logos-inner kmin-logos-inner--tagline">
            <span>{DESIGNERS_LOGOS_STRIP}</span>
          </div>
        </div>
      </section>

      <section id="benefits" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">Для кого</div>
            <h2 className="kmin-section-title">
              Опт для <span className="block text-white/50">фриланса и студий</span>
            </h2>
          </div>
          <div className="kmin-audience-grid">
            {DESIGNERS_AUDIENCE.map(item => (
              <div key={item.title} className="kmin-audience-card">
                <h3 className="kmin-feature-title">{item.title}</h3>
                <p className="kmin-feature-desc">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="kmin-section-header" style={{ marginTop: 64 }}>
            <div className="kmin-label">Оптовый доступ</div>
            <h3 className="kmin-section-title" style={{ fontSize: 'clamp(28px, 3vw, 40px)' }}>
              Что получаете
            </h3>
          </div>
          <div className="kmin-features-grid">
            {DESIGNERS_WHOLESALE.map(item => (
              <div key={item.title} className="kmin-feature-card kmin-feature-card--compact">
                <div className="kmin-feature-body">
                  <h4 className="kmin-feature-title">{item.title}</h4>
                  <p className="kmin-feature-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="tools" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">Инструменты</div>
            <h2 className="kmin-section-title">
              Всё для <span className="block text-white/50">клиентских проектов</span>
            </h2>
          </div>
          <div className="kmin-features-grid">
            {DESIGNERS_FEATURES.map((item, fi) => (
              <div key={item.title} className="kmin-feature-card">
                <div
                  className="kmin-feature-img"
                  onClick={() => setPreviewUrl(libImgs[5 + fi])}
                >
                  <img
                    src={libImgs[5 + fi]}
                    alt={`Пример: ${item.title}`}
                    className="kmin-preview-img"
                    loading="lazy"
                  />
                </div>
                <div className="kmin-feature-body">
                  <h4 className="kmin-feature-title">{item.title}</h4>
                  <p className="kmin-feature-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="kmin-examples-header" style={{ marginTop: 80, marginBottom: 48 }}>
            <div>
              <div className="kmin-label">Примеры</div>
              <h3 className="kmin-section-title" style={{ fontSize: 'clamp(28px, 3vw, 40px)' }}>
                Работы <span className="block text-white/50">в KartinAI</span>
              </h3>
            </div>
          </div>
          <div className="kmin-examples-grid">
            {Array.from({ length: LANDING_EXAMPLE_COUNT }, (_, ei) => {
              const meta = DESIGNERS_EXAMPLES[ei]
              return (
                <div key={meta.title} className="kmin-example-card">
                  <div className="kmin-example-img">
                    {ei === 0 ? (
                      <video
                        src="/landing/e987a576-b2a8-49b7-946a-d288914f66fc.mp4"
                        poster={libImgs[9]}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="kmin-preview-img"
                        onClick={() =>
                          setPreviewUrl('/landing/e987a576-b2a8-49b7-946a-d288914f66fc.mp4')
                        }
                      />
                    ) : ei === 1 ? (
                      <video
                        src="/landing/lipobaze_03.mp4"
                        poster="/landing/lipobaze_03.jpg"
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="kmin-preview-img"
                        onClick={() => setPreviewUrl('/landing/lipobaze_03.mp4')}
                      />
                    ) : (
                      <img
                        src={libImgs[9 + ei]}
                        alt={meta.title}
                        className="kmin-preview-img"
                        loading="lazy"
                        onClick={() => setPreviewUrl(libImgs[9 + ei])}
                      />
                    )}
                  </div>
                  <div className="kmin-example-body">
                    <div className="kmin-example-meta">
                      <span>AI Generated</span>
                      <span className="text-emerald-400">PRO</span>
                    </div>
                    <h4 className="kmin-feature-title">{meta.title}</h4>
                    <p className="kmin-example-desc">{meta.desc}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <section id="how" className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-section-header">
            <div className="kmin-label">Подключение</div>
            <h2 className="kmin-section-title">Как это работает</h2>
          </div>
          <div className="kmin-steps-grid">
            {DESIGNERS_STEPS.map(item => (
              <div key={item.step} className="kmin-step-card">
                <div className="kmin-step-num">{item.step}</div>
                <h3 className="kmin-feature-title">{item.title}</h3>
                <p className="kmin-feature-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="kmin-section kmin-section-alt">
        <div className="kmin-container">
          <div className="kmin-contact-grid">
            <div>
              <div className="kmin-label" style={{ justifyContent: 'flex-start' }}>
                Заявка
              </div>
              <h2 className="kmin-section-title" style={{ textAlign: 'left' }}>
                {DESIGNERS_CONTACT.title}
              </h2>
              <ul className="kmin-contact-list">
                {DESIGNERS_CONTACT.bullets.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              {telegramUrl && (
                <button
                  type="button"
                  className="kmin-btn-outline kmin-btn-lg"
                  style={{ marginTop: 24 }}
                  onClick={handleTelegramLead}
                >
                  Написать в Telegram
                </button>
              )}
            </div>

            <div className="kmin-lead-form-wrap">
              {formSent ? (
                <div className="kmin-lead-success">
                  <span className="material-symbols-outlined">check_circle</span>
                  <p>{DESIGNERS_CONTACT.formSuccess}</p>
                  <button
                    type="button"
                    className="kmin-btn-outline kmin-btn-block"
                    onClick={() => goRegister('contact_after_form')}
                  >
                    {DESIGNERS_HERO.secondaryCta}
                  </button>
                </div>
              ) : (
                <form className="kmin-lead-form" onSubmit={handleLeadSubmit}>
                  <label className="kmin-lead-label">
                    Имя
                    <input
                      className="kmin-lead-input"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      required
                      autoComplete="name"
                    />
                  </label>
                  <label className="kmin-lead-label">
                    Email или Telegram
                    <input
                      className="kmin-lead-input"
                      value={form.contact}
                      onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}
                      required
                      autoComplete="email"
                    />
                  </label>
                  <label className="kmin-lead-label">
                    Тип
                    <select
                      className="kmin-lead-input"
                      value={form.type}
                      onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                    >
                      {DESIGNERS_LEAD_TYPES.map(t => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="kmin-lead-label">
                    Комментарий
                    <textarea
                      className="kmin-lead-input kmin-lead-textarea"
                      rows={3}
                      value={form.comment}
                      onChange={e => setForm(f => ({ ...f, comment: e.target.value }))}
                      placeholder="Какие задачи закрываете, размер команды..."
                    />
                  </label>
                  <button type="submit" className="kmin-btn-primary kmin-btn-block">
                    Отправить заявку
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="kmin-section">
        <div className="kmin-container">
          <div className="kmin-cta-card">
            <div className="kmin-cta-glow" />
            <div className="kmin-cta-content">
              <h3 className="kmin-cta-title">{DESIGNERS_FINAL_CTA.title}</h3>
              <p className="kmin-cta-desc">{DESIGNERS_FINAL_CTA.desc}</p>
              <button
                type="button"
                className="kmin-btn-primary kmin-btn-cta"
                onClick={() => goRegister('final_cta')}
              >
                {DESIGNERS_FINAL_CTA.button}
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="kmin-footer">
        <div className="kmin-container">
          <div className="kmin-footer-inner">
            <div>
              <BrandLogo variant="landing-sm" to="/" />
              <div className="kmin-footer-sub">AI-платформа для дизайнеров и студий</div>
              <div className="kmin-footer-legal">
                <div>ИП Вихарев С.В.</div>
                <div>ИНН: 745202549414</div>
                <div>ОГРНИП: 320237500261078</div>
              </div>
            </div>
            <div className="kmin-footer-links">
              <Link to="/" className="kmin-footer-link-btn">
                Сайт для бизнеса
              </Link>
              {telegramUrl && (
                <button
                  type="button"
                  className="kmin-footer-link-btn"
                  onClick={handleTelegramLead}
                >
                  Telegram
                </button>
              )}
              {vkUrl && (
                <a href={vkUrl} target="_blank" rel="noopener noreferrer">
                  VK
                </a>
              )}
            </div>
          </div>
        </div>
      </footer>

      {previewUrl && (
        <div className="kmin-preview-overlay" onClick={() => setPreviewUrl(null)}>
          <button
            type="button"
            className="kmin-preview-close"
            onClick={() => setPreviewUrl(null)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
          {previewUrl.endsWith('.mp4') ? (
            <video
              src={previewUrl}
              autoPlay
              loop
              playsInline
              className="kmin-preview-full"
              controls
              onClick={e => e.stopPropagation()}
            />
          ) : (
            <img
              src={previewUrl}
              alt="Увеличенный предпросмотр"
              className="kmin-preview-full"
            />
          )}
        </div>
      )}
    </div>
  )
}
